import { observer } from 'mobx-react-lite';
import React, { useContext, useEffect, useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { RouteComponentProps } from 'react-router-dom';
import { combineValidators, isRequired } from 'revalidate';
import { Button, Form, Grid, Header, Segment } from 'semantic-ui-react';

import DateInput from '../../../app/common/form/DateInput';
import NumberInput from '../../../app/common/form/NumberInput';
import SelectInput from '../../../app/common/form/SelectInput';
import { addMonth, formatParameterDisp } from '../../../app/common/util/util';
import { DepositSalesTicketFormValues } from '../../../app/models/depositSalesTicket';
import { IOption } from '../../../app/models/option';
import { RootStoreContext } from '../../../app/stores/rootStore';

const validate = combineValidators({
  agentName: isRequired({ message: '委託先は必須入力です' }),
  mdTdTicketTypeName: isRequired({ message: 'テナントは必須入力です' }),
  startDate: isRequired({ message: '入場可能期間は必須入力です' }),
  limitMonthDate: isRequired({ message: '有効期限日は必須入力です' }),
  ticketDisplayName: isRequired({ message: '券種は必須入力です' }),
  ticketReserveTypeName: isRequired({ message: '発券区分は必須入力です' }),
});

interface DetailParams {
  id: string;
}

const DepositSalesTicketForm: React.FC<RouteComponentProps<DetailParams>> = ({
  match,
}) => {
  const rootStore = useContext(RootStoreContext);
  const { loadAgents, loadAgent, agentOptions } = rootStore.agentStore;
  const {
    ticketPriceOptions,
    loadTicketPrices,
    loadTicketPrice,
  } = rootStore.ticketPriceStore;
  const {
    mdTdTicketTypeOptions,
    ticketReserveTypeOptions,
    submitting,
    loadDepositSalesTicketAttributes,
    loadDepositSalesTicket,
    createDepositSalesTicket,
    editDepositSalesTicket,
  } = rootStore.depositSalesTicketStore;

  const [formValues, setFormValues] = useState(
    new DepositSalesTicketFormValues()
  );
  const [loading, setLoading] = useState(false);
  const [agentSelectedKey, setAgentSelectedKey] = useState('');
  const [tenantSelectedKey, setTenantSelectedKey] = useState('');
  const [startDateSelectedValue, setStartDateSelectedValue] = useState(null);
  const [ticketSelectedKey, setTicketSelectedKey] = useState('');
  const [reserveTypeSelectedKey, setReserveTypeSelectedKey] = useState('');

  useEffect(() => {
    loadAgents();
    loadDepositSalesTicketAttributes();
    if (match.params.id) {
      setLoading(true);
      loadDepositSalesTicket(match.params.id).then((depositSalesTicket) => {
        depositSalesTicket.registrationDate = new Date(
          depositSalesTicket.registrationDate
        );
        depositSalesTicket.startDate = new Date(depositSalesTicket.startDate);
        depositSalesTicket.limitMonthDate = new Date(
          depositSalesTicket.limitMonthDate
        );
        setFormValues(new DepositSalesTicketFormValues(depositSalesTicket));
        setAgentSelectedKey(depositSalesTicket.agentId);
        setTenantSelectedKey(depositSalesTicket.mdTdTicketType);
        setStartDateSelectedValue(depositSalesTicket.startDate);
        setTicketSelectedKey(depositSalesTicket.ticketUid);
        setReserveTypeSelectedKey(depositSalesTicket.ticketReserveType);
        loadTicketPrices(
          depositSalesTicket.agentId,
          formatParameterDisp(depositSalesTicket.startDate),
          depositSalesTicket.mdTdTicketType
        ).then(() => setLoading(false));
      });
    }
  }, [
    loadAgents,
    loadDepositSalesTicketAttributes,
    loadDepositSalesTicket,
    loadTicketPrices,
    match.params.id,
  ]);

  const handleFinalFormSubmit = (values: any) => {
    //画面選択項目をセット
    const { ...depositSalesTicket } = values;
    depositSalesTicket.registrationDate = formatParameterDisp(
      depositSalesTicket.registrationDate
    );
    depositSalesTicket.startDate = formatParameterDisp(
      depositSalesTicket.startDate
    );
    depositSalesTicket.limitMonthDate = formatParameterDisp(
      depositSalesTicket.limitMonthDate
    );
    depositSalesTicket.mdTdTicketType = tenantSelectedKey;
    depositSalesTicket.count = parseInt(depositSalesTicket.count, 10);
    depositSalesTicket.agentId = agentSelectedKey;
    depositSalesTicket.ticketUid = ticketSelectedKey;
    depositSalesTicket.ticketReserveType = reserveTypeSelectedKey;
    //マスタ項目をセット
    const agent = loadAgent(agentSelectedKey);
    depositSalesTicket.inboundFlag = agent.inboundFlag;
    depositSalesTicket.prefectureId = agent.prefectureId;
    const ticketPrice = loadTicketPrice(ticketSelectedKey);
    depositSalesTicket.priceSystemId = ticketPrice.priceSystemId;
    depositSalesTicket.ticketId = ticketPrice.ticketId;
    depositSalesTicket.priceType = ticketPrice.priceType;
    depositSalesTicket.ticketType = ticketPrice.ticketType;
    depositSalesTicket.ticketSpecType = ticketPrice.ticketSpecType;
    depositSalesTicket.displayOrder = ticketPrice.displayOrder;
    if (!depositSalesTicket.salesId) {
      createDepositSalesTicket(depositSalesTicket);
    } else {
      editDepositSalesTicket(depositSalesTicket);
    }
  };

  const initTenantChange = (state: any, changeValue: any) => {
    setTenantSelectedKey('');
    changeValue(state, 'mdTdTicketTypeName', () => '');
  };

  const initStartDateChange = (state: any, changeValue: any) => {
    setStartDateSelectedValue(null);
    changeValue(state, 'startDate', () => '');
    changeValue(state, 'limitMonthDate', () => '');
  };

  const initTicketChange = (state: any, changeValue: any) => {
    setTicketSelectedKey('');
    changeValue(state, 'ticketDisplayName', () => '');
  };

  const initCountChange = (state: any, changeValue: any) => {
    changeValue(state, 'count', () => 0);
  };

  const initPriceChange = (state: any, changeValue: any) => {
    changeValue(state, 'unitPrice', () => 0);
    changeValue(state, 'cashTotal', () => 0);
  };

  return (
    <Grid>
      <Grid.Column width={10}>
        <Segment clearing>
          <Header
            icon='keyboard'
            content={
              !match.params.id ? '新規預託チケット作成' : '預託チケット編集'
            }
          />
          <FinalForm
            validate={validate}
            initialValues={formValues}
            onSubmit={handleFinalFormSubmit}
            mutators={{
              handleAgentChange: ([, data], state, { changeValue }) => {
                const { key } = data.options.find(
                  (o: IOption) => o.value === data.value
                );
                setAgentSelectedKey(key);
                initTenantChange(state, changeValue);
                initStartDateChange(state, changeValue);
                initTicketChange(state, changeValue);
                initCountChange(state, changeValue);
                initPriceChange(state, changeValue);
                //入力された値で発券区分セット
                const agent = loadAgent(key);
                changeValue(
                  state,
                  'ticketReserveTypeName',
                  () => agent.relationTypeName
                );
                setReserveTypeSelectedKey(agent.relationType);
              },
              handleTenantChange: ([, data], state, { changeValue }) => {
                const { key } = data.options.find(
                  (o: IOption) => o.value === data.value
                );
                setTenantSelectedKey(key);
                initStartDateChange(state, changeValue);
                initTicketChange(state, changeValue);
                initCountChange(state, changeValue);
                initPriceChange(state, changeValue);
              },
              handleStartDateChange: ([event], state, { changeValue }) => {
                setStartDateSelectedValue(event);
                initTicketChange(state, changeValue);
                initCountChange(state, changeValue);
                initPriceChange(state, changeValue);
                if (event !== null) {
                  //入力された値で有効期限日セット
                  const agent = loadAgent(agentSelectedKey);
                  changeValue(state, 'limitMonthDate', () =>
                    addMonth(event, agent.limitMonthCount)
                  );
                  setLoading(true);
                  loadTicketPrices(
                    agentSelectedKey,
                    formatParameterDisp(event),
                    tenantSelectedKey
                  ).then(() => setLoading(false));
                } else {
                  changeValue(state, 'limitMonthDate', () => null);
                }
              },
              handleTicketChange: ([, data], state, { changeValue }) => {
                const { key } = data.options.find(
                  (o: IOption) => o.value === data.value
                );
                setTicketSelectedKey(key);
                initCountChange(state, changeValue);
                initPriceChange(state, changeValue);
                //入力された値で単価セット
                const ticketPrice = loadTicketPrice(key);
                changeValue(state, 'unitPrice', () => ticketPrice.unitPrice);
              },
              handleCountChange: ([event], state, { changeValue }) => {
                //入力された値で金額セット
                const ticketPrice = loadTicketPrice(ticketSelectedKey);
                changeValue(
                  state,
                  'cashTotal',
                  () => event.target.value * ticketPrice.unitPrice
                );
              },
              handleReserveTypeChange: ([, data]) => {
                const { key } = data.options.find(
                  (o: IOption) => o.value === data.value
                );
                setReserveTypeSelectedKey(key);
              },
            }}
            render={({ form, handleSubmit, invalid, pristine }) => (
              <Form onSubmit={handleSubmit} loading={loading}>
                <Field
                  readOnly={true}
                  name='registrationDate'
                  label='作成日'
                  value={formValues.registrationDate}
                  component={DateInput}
                />
                <Field
                  name='agentName'
                  label='委託先'
                  options={agentOptions}
                  value={formValues.agentName}
                  component={SelectInput}
                  inputOnChange={form.mutators.handleAgentChange}
                />
                <Field
                  disabled={agentSelectedKey === ''}
                  name='mdTdTicketTypeName'
                  label='テナント'
                  options={mdTdTicketTypeOptions}
                  value={formValues.mdTdTicketTypeName}
                  component={SelectInput}
                  inputOnChange={form.mutators.handleTenantChange}
                />
                <Form.Group>
                  <Field
                    disabled={
                      agentSelectedKey === '' || tenantSelectedKey === ''
                    }
                    name='startDate'
                    label='入場可能期間'
                    value={formValues.startDate}
                    component={DateInput}
                    inputOnChange={form.mutators.handleStartDateChange}
                  />
                  <Form.Field>
                    <label>～</label>
                  </Form.Field>
                  <Field
                    disabled={
                      agentSelectedKey === '' || tenantSelectedKey === ''
                    }
                    name='limitMonthDate'
                    label='有効期限日'
                    value={formValues.limitMonthDate}
                    component={DateInput}
                    minDate={startDateSelectedValue!}
                  />
                </Form.Group>
                <Field
                  disabled={
                    agentSelectedKey === '' ||
                    tenantSelectedKey === '' ||
                    startDateSelectedValue === null
                  }
                  name='ticketDisplayName'
                  label='券種'
                  options={ticketPriceOptions}
                  value={formValues.ticketDisplayName}
                  component={SelectInput}
                  inputOnChange={form.mutators.handleTicketChange}
                />
                <Form.Group widths='equal'>
                  <Field
                    disabled={ticketSelectedKey === ''}
                    name='count'
                    label='発行枚数'
                    value={formValues.count}
                    component={NumberInput}
                    inputOnChange={form.mutators.handleCountChange}
                  />
                  <Field
                    disabled={ticketSelectedKey === ''}
                    readOnly={true}
                    name='unitPrice'
                    label='単価'
                    value={formValues.unitPrice}
                    component={NumberInput}
                  />
                  <Field
                    disabled={ticketSelectedKey === ''}
                    readOnly={true}
                    name='cashTotal'
                    label='金額'
                    value={formValues.cashTotal}
                    component={NumberInput}
                  />
                </Form.Group>
                <Field
                  disabled={agentSelectedKey === ''}
                  name='ticketReserveTypeName'
                  label='発券区分'
                  options={ticketReserveTypeOptions}
                  value={formValues.ticketReserveTypeName}
                  component={SelectInput}
                  inputOnChange={form.mutators.handleReserveTypeChange}
                />
                <Button
                  loading={submitting}
                  disabled={loading || invalid || pristine}
                  floated='right'
                  positive
                  type='submit'
                  content='登録'
                />
              </Form>
            )}
          />
        </Segment>
      </Grid.Column>
    </Grid>
  );
};

export default observer(DepositSalesTicketForm);
